import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ShoppingCart, Plus, Minus, Trash2, Search, X,
  CreditCard, Banknote, CheckCircle, Printer, RotateCcw,
  Tag, User, Phone, FileText, Pause, Play, LayoutGrid,
  List, Smartphone, HelpCircle, UtensilsCrossed, Package,
  Bike, MessageSquare, ChevronDown, ChevronUp, Percent,
  Hash, Clock, TrendingUp, Delete, Star, ScanLine,
  TableProperties, Split, AlertTriangle, ChevronRight,
} from "lucide-react";
import { useAdmin } from "../admin-context";
import { useAppContext } from "../../../context";
import { apiUrl } from "../../../utils/config";

const API_BASE = apiUrl;
const LICENSE_ID = 3;
const CASHIER_ID = 2;

// ── helpers ───────────────────────────────────────────────────
const getImg = (p) => p?.images?.image || p?.image || null;

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const Badge = ({ children, active, onClick }) => (
  <button type="button" onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all
      ${active ? "bg-orange-500 text-white shadow-sm" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600"}`}>
    {children}
  </button>
);

const METHOD_OPTS = [
  { key: "cash",  label: "Cash",  Icon: Banknote  },
  { key: "card",  label: "Card",  Icon: CreditCard },
  { key: "upi",   label: "UPI",   Icon: Smartphone },
  { key: "other", label: "Other", Icon: HelpCircle },
];

const ORDER_TYPES = [
  { key: "dine-in",  label: "Dine In",  Icon: UtensilsCrossed },
  { key: "takeaway", label: "Takeaway", Icon: Package          },
  { key: "delivery", label: "Delivery", Icon: Bike             },
];

const TIP_PRESETS = [5, 10, 15, 18, 20];
const TABLES = Array.from({ length: 20 }, (_, i) => i + 1);

// ── Live Clock ────────────────────────────────────────────────
const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div className="flex items-center gap-1.5 text-gray-400 text-xs">
      <Clock size={12} />
      <span className="font-bold text-gray-700">{now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span>
      <span>·</span>
      <span>{now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
    </div>
  );
};

// ── Session Stats ─────────────────────────────────────────────
const SessionStats = ({ count, revenue, formatPrice }) => (
  <div className="grid grid-cols-2 gap-3 flex-shrink-0">
    <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm">
      <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
        <ShoppingCart size={16} className="text-orange-500" />
      </div>
      <div>
        <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold leading-none mb-0.5">Orders Today</p>
        <p className="text-xl font-extrabold text-gray-900 leading-none">{count}</p>
      </div>
    </div>
    <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm">
      <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
        <TrendingUp size={16} className="text-green-500" />
      </div>
      <div>
        <p className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold leading-none mb-0.5">Session Revenue</p>
        <p className="text-xl font-extrabold text-orange-600 leading-none">{formatPrice(revenue)}</p>
      </div>
    </div>
  </div>
);

// ── Numpad ────────────────────────────────────────────────────
const Numpad = ({ value, onChange, label }) => {
  const press = (key) => {
    if (key === "⌫") onChange(value.slice(0, -1));
    else if (key === ".") { if (!value.includes(".")) onChange(value + "."); }
    else if (key === "00") onChange(value === "" ? "" : value + "00");
    else onChange(value + key);
  };
  const keys = ["1","2","3","4","5","6","7","8","9","00","0","⌫"];
  return (
    <div className="space-y-2">
      <div className="bg-gray-900 text-white rounded-xl px-4 py-3 text-right">
        <p className="text-[10px] text-gray-500 mb-0.5">{label}</p>
        <p className="text-2xl font-black tracking-wide">{value || "0.00"}</p>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {keys.map((k) => (
          <button key={k} type="button" onClick={() => press(k)}
            className={`py-3 rounded-xl text-sm font-bold transition active:scale-95
              ${k === "⌫" ? "bg-red-50 border border-red-100 text-red-500 hover:bg-red-100"
                          : "bg-gray-50 border border-gray-200 text-gray-800 hover:bg-orange-50 hover:border-orange-200 hover:text-orange-700"}`}>
            {k === "⌫" ? <Delete size={16} className="mx-auto" /> : k}
          </button>
        ))}
        <button type="button" onClick={() => press(".")}
          className="py-3 rounded-xl text-sm font-bold bg-gray-50 border border-gray-200 text-gray-800 hover:bg-orange-50 hover:border-orange-200 hover:text-orange-700 transition active:scale-95">.</button>
        <button type="button" onClick={() => onChange("")}
          className="col-span-2 py-3 rounded-xl text-xs font-bold bg-gray-100 border border-gray-200 text-gray-500 hover:bg-gray-200 transition active:scale-95">Clear</button>
      </div>
    </div>
  );
};

// ── Table Picker ──────────────────────────────────────────────
const TablePicker = ({ value, onChange, onClose }) => (
  <>
    <div className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm" onClick={onClose} />
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><TableProperties size={16} className="text-orange-500" /> Select Table</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500" type="button"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {TABLES.map((t) => (
            <button key={t} type="button" onClick={() => { onChange(String(t)); onClose(); }}
              className={`aspect-square rounded-xl text-sm font-bold transition-all
                ${String(value) === String(t) ? "bg-orange-500 text-white shadow-md" : "bg-gray-50 border border-gray-200 text-gray-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-600"}`}>
              {t}
            </button>
          ))}
        </div>
        {value && (
          <button type="button" onClick={() => { onChange(""); onClose(); }}
            className="mt-3 w-full text-xs text-red-400 hover:text-red-600 py-2 border border-red-100 rounded-xl hover:bg-red-50 transition">
            Clear table selection
          </button>
        )}
      </div>
    </div>
  </>
);

// ── Product Card (grid) ───────────────────────────────────────
const ProductCard = ({ product, onAdd, qty, onFav, isFav, formatPrice }) => {
  const img     = getImg(product);
  const inStock = product.stock === undefined || product.stock === null || product.stock > 0;
  return (
    <button type="button" onClick={() => inStock && onAdd(product)} disabled={!inStock}
      className={`group relative flex flex-col bg-white rounded-2xl border overflow-hidden transition-all duration-200 text-left
        ${!inStock ? "opacity-60 cursor-not-allowed border-gray-100"
          : qty ? "border-orange-400 shadow-lg shadow-orange-100/60 scale-[1.01]"
                : "border-gray-100 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-100/40 hover:scale-[1.01]"}`}>
      {/* Image */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
        {img
          ? <img src={img} alt={product.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
          : <div className="w-full h-full flex items-center justify-center">
              <UtensilsCrossed size={28} className="text-gray-200" />
            </div>
        }
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        {/* Out of stock overlay */}
        {!inStock && (
          <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
            <span className="bg-white text-gray-700 text-[10px] font-bold px-2 py-1 rounded-full">Out of Stock</span>
          </div>
        )}
        {/* qty badge */}
        {qty > 0 && (
          <div className="absolute top-2 left-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-orange-500 text-white flex items-center justify-center text-[11px] font-bold shadow-md">
            {qty}
          </div>
        )}
        {/* quick-add button */}
        {inStock && (
          <div className={`absolute top-2 right-2 w-7 h-7 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-md transition-all duration-150
            ${qty ? "opacity-100 scale-100" : "opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100"}`}>
            <Plus size={14} />
          </div>
        )}
        {/* fav */}
        <button type="button" onClick={(e) => { e.stopPropagation(); onFav(product.id); }}
          className={`absolute bottom-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all
            ${isFav ? "bg-amber-400 text-white opacity-100" : "bg-white/80 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-amber-400"}`}>
          <Star size={11} fill={isFav ? "currentColor" : "none"} />
        </button>
        {/* stock low warning */}
        {inStock && product.stock !== undefined && product.stock <= 5 && product.stock > 0 && (
          <div className="absolute bottom-2 left-2">
            <span className="bg-amber-400 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{product.stock} left</span>
          </div>
        )}
      </div>
      {/* Info */}
      <div className="p-2.5">
        <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-1 mb-0.5">{product.name}</p>
        {product.category_name && <p className="text-[10px] text-gray-400 mb-1">{product.category_name}</p>}
        <p className="text-sm font-extrabold text-orange-500">{formatPrice(product.price)}</p>
      </div>
    </button>
  );
};

// ── Product Row (list) ────────────────────────────────────────
const ProductRow = ({ product, onAdd, qty, onFav, isFav, formatPrice }) => {
  const img     = getImg(product);
  const inStock = product.stock === undefined || product.stock === null || product.stock > 0;
  return (
    <button type="button" onClick={() => inStock && onAdd(product)} disabled={!inStock}
      className={`flex items-center gap-3 w-full bg-white rounded-xl border px-3 py-2.5 text-left transition-all
        ${!inStock ? "opacity-60 cursor-not-allowed border-gray-100"
          : qty ? "border-orange-300 shadow-sm shadow-orange-100" : "border-gray-100 hover:border-orange-200 hover:shadow-sm"}`}>
      <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center shrink-0">
        {img ? <img src={img} alt={product.name} className="w-full h-full object-cover" />
             : <UtensilsCrossed size={18} className="text-gray-200" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{product.name}</p>
        {product.category_name && <p className="text-[10px] text-gray-400">{product.category_name}</p>}
        {inStock && product.stock !== undefined && product.stock <= 5 && product.stock > 0 && (
          <p className="text-[10px] text-amber-500 font-semibold">{product.stock} left</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={(e) => { e.stopPropagation(); onFav(product.id); }}
          className={isFav ? "text-amber-400" : "text-gray-200 hover:text-amber-300"}>
          <Star size={13} fill={isFav ? "currentColor" : "none"} />
        </button>
        {!inStock && <span className="text-[10px] text-gray-400 font-semibold">Out of stock</span>}
        {qty > 0 && <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">{qty}</span>}
        <span className="text-sm font-bold text-orange-500">{formatPrice(product.price)}</span>
      </div>
    </button>
  );
};

// ── Order Item Row ────────────────────────────────────────────
const OrderItemRow = ({ item, onQty, onRemove, onNoteChange, formatPrice }) => {
  const [showNote, setShowNote] = useState(!!item.note);
  const [editQty,  setEditQty]  = useState(false);
  const qtyRef = useRef();
  const img    = getImg(item);

  const startEdit  = () => { setEditQty(true); setTimeout(() => qtyRef.current?.select(), 30); };
  const commitQty  = (val) => { const n = parseInt(val, 10); if (n >= 1) onQty(item.id, n - item.quantity); setEditQty(false); };

  return (
    <div className="px-3 py-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-center gap-2.5">
        {/* thumbnail */}
        <div className="w-9 h-9 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center shrink-0">
          {img ? <img src={img} alt={item.name} className="w-full h-full object-cover" />
               : <UtensilsCrossed size={13} className="text-gray-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-800 truncate leading-tight">{item.name}</p>
          <p className="text-[11px] text-orange-500 font-medium">{formatPrice(item.price)} ea</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => onQty(item.id, -1)} disabled={item.quantity <= 1}
            className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-200 hover:bg-orange-50 hover:border-orange-300 disabled:opacity-30 transition-colors">
            <Minus size={10} />
          </button>
          {editQty ? (
            <input ref={qtyRef} type="number" defaultValue={item.quantity}
              onBlur={(e) => commitQty(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") commitQty(e.target.value); if (e.key === "Escape") setEditQty(false); }}
              className="w-9 text-center text-sm font-bold border border-orange-300 rounded-lg outline-none" />
          ) : (
            <button type="button" onClick={startEdit}
              className="w-7 text-center text-sm font-bold text-gray-800 hover:text-orange-600 transition cursor-text">
              {item.quantity}
            </button>
          )}
          <button type="button" onClick={() => onQty(item.id, 1)}
            className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-200 hover:bg-orange-50 hover:border-orange-300 transition-colors">
            <Plus size={10} />
          </button>
        </div>
        <span className="text-xs font-bold text-gray-700 min-w-[3.5rem] text-right">{formatPrice(item.price * item.quantity)}</span>
        <button type="button" onClick={() => setShowNote(v => !v)}
          className={`p-1 rounded transition ${showNote || item.note ? "text-orange-400" : "text-gray-300 hover:text-gray-500"}`}>
          <MessageSquare size={12} />
        </button>
        <button type="button" onClick={() => onRemove(item.id)} className="p-1 text-gray-300 hover:text-red-400 transition">
          <Trash2 size={12} />
        </button>
      </div>
      {showNote && (
        <input type="text" placeholder="Note for kitchen…" value={item.note || ""}
          onChange={(e) => onNoteChange(item.id, e.target.value)}
          className="mt-1.5 ml-11 w-[calc(100%-2.75rem)] text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-300 placeholder:text-gray-300 bg-gray-50" />
      )}
    </div>
  );
};

// ── Held Orders ───────────────────────────────────────────────
const HeldOrdersPanel = ({ held, onRecall, onDiscard }) => {
  if (!held.length) return null;
  return (
    <div className="flex-shrink-0 bg-amber-50 border-t border-amber-100 px-3 py-2.5">
      <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wide mb-2 flex items-center gap-1">
        <Pause size={10} /> {held.length} held order{held.length !== 1 ? "s" : ""}
      </p>
      <div className="flex gap-2 overflow-x-auto pb-0.5">
        {held.map((h, i) => (
          <div key={i} className="flex-shrink-0 bg-white border border-amber-200 rounded-xl px-3 py-2 min-w-[100px]">
            <p className="text-xs font-bold text-gray-800 truncate">{h.customerName || `Order ${i + 1}`}</p>
            <p className="text-[10px] text-gray-400">{h.items.length} item{h.items.length !== 1 ? "s" : ""} · {h.orderType}</p>
            <div className="flex gap-1 mt-1.5">
              <button type="button" onClick={() => onRecall(i)}
                className="flex-1 text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-lg py-1 hover:bg-amber-100 flex items-center justify-center gap-1">
                <Play size={9} /> Recall
              </button>
              <button type="button" onClick={() => onDiscard(i)} className="w-6 flex items-center justify-center text-gray-300 hover:text-red-400">
                <X size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Receipt ───────────────────────────────────────────────────
const Receipt = ({ order, onNewOrder, formatPrice }) => (
  <div className="flex flex-col items-center gap-4 py-4">
    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center shadow-lg shadow-green-100">
      <CheckCircle size={36} className="text-green-500" />
    </div>
    <div className="text-center">
      <p className="text-lg font-extrabold text-gray-900">Sale Complete</p>
      <p className="text-xs text-gray-400 mt-0.5">Order #{order.id}</p>
      {order.orderType && (
        <span className="inline-block mt-1 text-[10px] font-semibold bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full capitalize">
          {order.orderType}
        </span>
      )}
    </div>
    <div className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 space-y-1.5 text-sm">
      {order.customer && (
        <div className="flex justify-between text-xs text-gray-500 pb-1">
          <span>Customer</span><span>{order.customer}{order.phone ? ` · ${order.phone}` : ""}</span>
        </div>
      )}
      {order.tableNo && <div className="flex justify-between text-xs text-gray-500 pb-1"><span>Table</span><span>#{order.tableNo}</span></div>}
      {order.items.map((item, i) => (
        <div key={i}>
          <div className="flex justify-between">
            <span className="text-gray-700">{item.name} × {item.quantity}</span>
            <span className="font-medium text-gray-800">{formatPrice(item.price * item.quantity)}</span>
          </div>
          {item.note && <p className="text-[10px] text-gray-400 ml-2">↳ {item.note}</p>}
        </div>
      ))}
      {order.note && <div className="bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 text-xs text-amber-700 mt-1">Note: {order.note}</div>}
      <div className="border-t border-gray-200 my-2" />
      {order.discount > 0 && <div className="flex justify-between text-green-600 text-xs"><span>Discount</span><span>−{formatPrice(order.discount)}</span></div>}
      {order.tip > 0 && <div className="flex justify-between text-blue-600 text-xs"><span>Tip</span><span>+{formatPrice(order.tip)}</span></div>}
      {order.tax > 0 && <div className="flex justify-between text-gray-500 text-xs"><span>Tax</span><span>{formatPrice(order.tax)}</span></div>}
      <div className="flex justify-between font-extrabold text-gray-900 text-base pt-1">
        <span>Total</span><span className="text-orange-600">{formatPrice(order.total)}</span>
      </div>
      {order.splitPayments?.length > 1 ? (
        order.splitPayments.map((sp, i) => (
          <div key={i} className="flex justify-between text-gray-400 text-xs">
            <span className="capitalize">{sp.method}</span><span>{formatPrice(sp.amount)}</span>
          </div>
        ))
      ) : (
        <>
          {order.method === "cash" && order.tendered > 0 && (
            <>
              <div className="flex justify-between text-gray-500 text-xs"><span>Tendered</span><span>{formatPrice(order.tendered)}</span></div>
              <div className="flex justify-between font-bold text-blue-600 text-xs"><span>Change</span><span>{formatPrice(Math.max(0, order.tendered - order.total))}</span></div>
            </>
          )}
          <div className="flex justify-between text-gray-400 text-xs pt-1"><span>Paid via</span><span className="capitalize">{order.method}</span></div>
        </>
      )}
    </div>
    <div className="flex gap-3 w-full">
      <button type="button" onClick={() => window.print()}
        className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-xl py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition">
        <Printer size={15} /> Print
      </button>
      <button type="button" onClick={onNewOrder}
        className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-2.5 text-sm font-bold transition shadow-sm">
        <RotateCcw size={15} /> New Order
      </button>
    </div>
  </div>
);

// ── Split Payment Panel ───────────────────────────────────────
const SplitPaymentPanel = ({ total, splitPayments, setSplitPayments, formatPrice }) => {
  const paid    = splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const remaining = Math.max(0, total - paid);

  const update = (i, field, val) =>
    setSplitPayments(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

  const add = () => setSplitPayments(prev => [...prev, { method: "card", amount: remaining.toFixed(2) }]);
  const remove = (i) => setSplitPayments(prev => prev.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {splitPayments.map((sp, i) => (
        <div key={i} className="flex gap-2 items-center">
          <select value={sp.method} onChange={(e) => update(i, "method", e.target.value)}
            className="text-xs border border-gray-200 rounded-xl px-2 py-2 bg-white outline-none focus:border-orange-300 flex-shrink-0">
            {METHOD_OPTS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <input type="number" min="0" step="0.01" value={sp.amount}
            onChange={(e) => update(i, "amount", e.target.value)}
            className="flex-1 text-xs border border-gray-200 rounded-xl px-3 py-2 outline-none focus:border-orange-300 min-w-0" />
          {splitPayments.length > 1 && (
            <button type="button" onClick={() => remove(i)} className="text-gray-300 hover:text-red-400 p-1">
              <X size={13} />
            </button>
          )}
        </div>
      ))}
      {remaining > 0.01 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-amber-600 font-semibold flex items-center gap-1"><AlertTriangle size={11} /> Remaining: {formatPrice(remaining)}</span>
          <button type="button" onClick={add} className="text-orange-600 font-semibold hover:underline flex items-center gap-1">
            <Plus size={11} /> Add payment
          </button>
        </div>
      )}
      {remaining <= 0.01 && paid > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-green-600 font-semibold">
          <CheckCircle size={12} /> Fully covered
        </div>
      )}
    </div>
  );
};

// ── Main POS Page ─────────────────────────────────────────────
const POSPage = () => {
  const { products, categories, showToast, logAudit, loadOrders } = useAdmin();
  const { formatPrice, storeSettings } = useAppContext();
  const taxRate = Number(storeSettings?.tax_rate || 0) / 100;

  // product browser
  const [search,         setSearch]        = useState("");
  const [activeCategory, setCategory]      = useState("all");
  const [viewMode,       setViewMode]      = useState("grid");
  const [favourites,     setFavourites]    = useState(new Set());
  const [barcodeQuery,   setBarcodeQuery]  = useState("");
  const searchRef = useRef();
  const barcodeRef = useRef();

  // order state
  const [orderItems,     setOrderItems]    = useState([]);
  const [orderType,      setOrderType]     = useState("dine-in");
  const [tableNo,        setTableNo]       = useState("");
  const [showTablePicker,setShowTable]     = useState(false);
  const [customerName,   setCustomer]      = useState("");
  const [customerPhone,  setPhone]         = useState("");
  const [orderNote,      setOrderNote]     = useState("");
  const [showOrderNote,  setShowOrderNote] = useState(false);
  const [discountVal,    setDiscount]      = useState("");
  const [discountType,   setDiscountType]  = useState("fixed");
  const [tipVal,         setTip]           = useState("");
  const [tipType,        setTipType]       = useState("percent");
  const [method,         setMethod]        = useState("cash");
  const [tendered,       setTendered]      = useState("");
  const [showNumpad,     setShowNumpad]    = useState(false);
  const [splitMode,      setSplitMode]     = useState(false);
  const [splitPayments,  setSplitPayments] = useState([{ method: "cash", amount: "" }]);
  const [charging,       setCharging]      = useState(false);
  const [receipt,        setReceipt]       = useState(null);

  // held orders & session
  const [heldOrders,     setHeldOrders]    = useState([]);
  const [sessionCount,   setSessionCount]  = useState(0);
  const [sessionRevenue, setSessionRevenue]= useState(0);

  const toggleFav = useCallback((id) => {
    setFavourites(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }, []);

  // barcode / name quick-add
  const handleBarcodeAdd = useCallback((e) => {
    if (e.key !== "Enter") return;
    const q = barcodeQuery.trim().toLowerCase();
    if (!q) return;
    const match = products.find(p =>
      String(p.sku || "").toLowerCase() === q ||
      p.name?.toLowerCase() === q
    );
    if (match) { addItem(match); setBarcodeQuery(""); showToast(`Added: ${match.name}`, "success"); }
    else showToast("No product found for that query.", "error");
  }, [barcodeQuery, products]);

  const filtered = useMemo(() => {
    let list = products;
    if (activeCategory === "__favs__") return [...products].filter(p => favourites.has(p.id));
    if (activeCategory !== "all") list = list.filter(p => String(p.category_id) === String(activeCategory));
    if (search.trim()) { const q = search.toLowerCase(); list = list.filter(p => p.name?.toLowerCase().includes(q)); }
    return list;
  }, [products, activeCategory, search, favourites]);

  // calculations
  const subtotal    = orderItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const discountAmt = useMemo(() => {
    const v = Number(discountVal) || 0;
    if (discountType === "percent") return parseFloat(Math.min(subtotal * v / 100, subtotal).toFixed(2));
    return Math.min(v, subtotal);
  }, [discountVal, discountType, subtotal]);
  const taxable = subtotal - discountAmt;
  const tax     = parseFloat((taxable * taxRate).toFixed(2));
  const tipAmt  = useMemo(() => {
    const v = Number(tipVal) || 0;
    if (!v) return 0;
    if (tipType === "percent") return parseFloat(((taxable + tax) * v / 100).toFixed(2));
    return v;
  }, [tipVal, tipType, taxable, tax]);
  const total  = parseFloat((taxable + tax + tipAmt).toFixed(2));
  const change = Math.max(0, (Number(tendered) || 0) - total);

  // split payment ready check
  const splitReady = useMemo(() => {
    if (!splitMode) return true;
    const paid = splitPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return paid >= total - 0.01;
  }, [splitMode, splitPayments, total]);

  // sync split first entry when total changes
  useEffect(() => {
    if (splitMode && splitPayments.length === 1)
      setSplitPayments([{ ...splitPayments[0], amount: total > 0 ? total.toFixed(2) : "" }]);
  }, [total, splitMode]);

  const addItem = (product) => {
    setOrderItems(prev => {
      const ex = prev.find(i => i.id === product.id);
      if (ex) return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...product, quantity: 1, note: "" }];
    });
  };
  const updateQty  = (id, delta) => setOrderItems(prev => prev.map(i => i.id === id ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i));
  const removeItem = (id)        => setOrderItems(prev => prev.filter(i => i.id !== id));
  const updateNote = (id, note)  => setOrderItems(prev => prev.map(i => i.id === id ? { ...i, note } : i));
  const totalItems = orderItems.reduce((s, i) => s + i.quantity, 0);

  const holdOrder = () => {
    if (!orderItems.length) return;
    setHeldOrders(prev => [...prev, { items: orderItems, orderType, tableNo, customerName, customerPhone, orderNote, discountVal, discountType, tipVal, tipType, method }]);
    clearOrder();
    showToast("Order held.", "info");
  };
  const recallOrder = (idx) => {
    const h = heldOrders[idx];
    setOrderItems(h.items); setOrderType(h.orderType); setTableNo(h.tableNo);
    setCustomer(h.customerName); setPhone(h.customerPhone); setOrderNote(h.orderNote);
    setDiscount(h.discountVal); setDiscountType(h.discountType);
    setTip(h.tipVal); setTipType(h.tipType); setMethod(h.method);
    setHeldOrders(prev => prev.filter((_, i) => i !== idx));
  };
  const discardHeld = (idx) => setHeldOrders(prev => prev.filter((_, i) => i !== idx));

  const clearOrder = () => {
    setOrderItems([]); setOrderType("dine-in"); setTableNo(""); setCustomer(""); setPhone("");
    setOrderNote(""); setShowOrderNote(false); setDiscount(""); setDiscountType("fixed");
    setTip(""); setTipType("percent"); setTendered(""); setMethod("cash");
    setSplitMode(false); setSplitPayments([{ method: "cash", amount: "" }]);
    setReceipt(null); setShowNumpad(false);
  };

  const handleCharge = async () => {
    if (!orderItems.length) return;
    if (!splitMode && method === "cash" && (Number(tendered) || 0) < total) {
      showToast("Cash tendered is less than the total.", "error"); return;
    }
    if (splitMode && !splitReady) {
      showToast("Split payment doesn't cover the total.", "error"); return;
    }
    setCharging(true);
    try {
      const payload = {
        status:         "delivered",
        total_discount: discountAmt,
        license_id:     LICENSE_ID,
        cashier_id:     CASHIER_ID,
        customer_name:  customerName  || undefined,
        customer_phone: customerPhone || undefined,
        order_type:     orderType,
        table_no:       tableNo       || undefined,
        order_note:     orderNote     || undefined,
        payment_method: splitMode ? "split" : method,
        tip:            tipAmt        || undefined,
        items: orderItems.map(i => ({
          menu_item_id: i.id, quantity: i.quantity, price: i.price, discount: 0,
          note: i.note || undefined, item: null,
        })),
      };
      const res   = await axios.post(`${API_BASE}/bill`, payload);
      const newId = res.data?.message?.bill_id || res.data?.id || Date.now();

      logAudit("POS Sale", "Order", `#${newId} — ${formatPrice(total)}`);
      showToast("Sale completed!", "success");
      loadOrders();
      setSessionCount(c => c + 1);
      setSessionRevenue(r => r + total);

      setReceipt({
        id: newId, items: orderItems.map(i => ({ name: i.name, price: Number(i.price), quantity: i.quantity, note: i.note })),
        discount: discountAmt, tip: tipAmt, tax, total, method: splitMode ? "split" : method,
        orderType, tableNo, note: orderNote, customer: customerName, phone: customerPhone,
        tendered: Number(tendered) || 0,
        splitPayments: splitMode ? splitPayments : undefined,
      });
    } catch (err) {
      console.error("POS charge failed:", err);
      showToast("Failed to process sale. Please try again.", "error");
    } finally {
      setCharging(false);
    }
  };

  const inOrderQty = (id) => orderItems.find(i => i.id === id)?.quantity || 0;

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex gap-4 h-[calc(100vh-7.5rem)] min-h-0">

      {showTablePicker && <TablePicker value={tableNo} onChange={setTableNo} onClose={() => setShowTable(false)} />}

      {/* ── LEFT: Product Browser ──────────────────────────── */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-3 overflow-hidden">

        {/* Top bar */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Point of Sale</h1>
            <LiveClock />
          </div>
          <div className="flex items-center gap-2">
            {orderItems.length > 0 && (
              <button type="button" onClick={holdOrder}
                className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-xl hover:bg-amber-100 transition font-semibold">
                <Pause size={12} /> Hold
              </button>
            )}
            {orderItems.length > 0 && (
              <button type="button" onClick={clearOrder}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl hover:bg-red-50 hover:border-red-200 transition">
                <RotateCcw size={13} /> Clear
              </button>
            )}
            <div className="flex border border-gray-200 rounded-xl overflow-hidden bg-white">
              <button type="button" onClick={() => setViewMode("grid")}
                className={`p-2 transition ${viewMode === "grid" ? "bg-orange-500 text-white" : "text-gray-400 hover:text-gray-600"}`}>
                <LayoutGrid size={14} />
              </button>
              <button type="button" onClick={() => setViewMode("list")}
                className={`p-2 transition ${viewMode === "list" ? "bg-orange-500 text-white" : "text-gray-400 hover:text-gray-600"}`}>
                <List size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Session stats */}
        <SessionStats count={sessionCount} revenue={sessionRevenue} formatPrice={formatPrice} />

        {/* Search + Barcode row */}
        <div className="flex gap-2 flex-shrink-0">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input ref={searchRef} type="text" placeholder="Search items…" value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition" />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            )}
          </div>
          {/* Barcode / quick-add */}
          <div className="relative w-44">
            <ScanLine size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input ref={barcodeRef} type="text" placeholder="Scan / SKU…" value={barcodeQuery}
              onChange={e => setBarcodeQuery(e.target.value)}
              onKeyDown={handleBarcodeAdd}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 transition" />
          </div>
        </div>

        {/* Product browser — categories + grid in one bounded scroll section */}
        <div className="flex-1 min-h-0 flex flex-col gap-2 bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">

          {/* Category tabs */}
          <div className="flex gap-2 overflow-x-auto px-3 pt-3 pb-1 flex-shrink-0 scrollbar-thin">
            <Badge active={activeCategory === "all"} onClick={() => setCategory("all")}>All</Badge>
            {favourites.size > 0 && (
              <Badge active={activeCategory === "__favs__"} onClick={() => setCategory("__favs__")}>
                ⭐ Favourites ({favourites.size})
              </Badge>
            )}
            {categories.map(cat => (
              <Badge key={cat.id} active={String(activeCategory) === String(cat.id)} onClick={() => setCategory(String(cat.id))}>
                {cat.name}
              </Badge>
            ))}
          </div>

          {/* Cart summary bar */}
          {orderItems.length > 0 && (
            <div className="mx-3 flex-shrink-0 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2 flex items-center gap-3">
              <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center shrink-0">
                <ShoppingCart size={13} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-gray-800">{totalItems} item{totalItems !== 1 ? "s" : ""} in order</p>
                <p className="text-[10px] text-gray-400 truncate leading-tight">
                  {orderItems.slice(0, 3).map(i => `${i.quantity}× ${i.name}`).join(", ")}
                  {orderItems.length > 3 ? ` +${orderItems.length - 3} more` : ""}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-extrabold text-orange-600">{formatPrice(total)}</p>
                {discountAmt > 0 && <p className="text-[10px] text-green-600">−{formatPrice(discountAmt)} off</p>}
              </div>
            </div>
          )}

          {/* Product grid / list — independent scroll */}
          <div className="flex-1 overflow-y-auto min-h-0 px-3 pb-3 scrollbar-thin">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-300 gap-2">
                <UtensilsCrossed size={32} />
                <p className="text-sm">No items found</p>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 pt-1">
                {filtered.map(product => (
                  <ProductCard key={product.id} product={product} onAdd={addItem}
                    qty={inOrderQty(product.id)} onFav={toggleFav} isFav={favourites.has(product.id)} formatPrice={formatPrice} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-2 pt-1">
                {filtered.map(product => (
                  <ProductRow key={product.id} product={product} onAdd={addItem}
                    qty={inOrderQty(product.id)} onFav={toggleFav} isFav={favourites.has(product.id)} formatPrice={formatPrice} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── RIGHT: Order Panel ─────────────────────────────── */}
      <div className="w-80 xl:w-96 flex-shrink-0 flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <ShoppingCart size={15} className="text-orange-500" /> Current Order
          </h2>
          {totalItems > 0 && (
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm shadow-orange-200">{totalItems}</span>
          )}
        </div>

        {receipt ? (
          <div className="flex-1 overflow-y-auto p-4">
            <Receipt order={receipt} onNewOrder={clearOrder} formatPrice={formatPrice} />
          </div>
        ) : (
          <>
            {/* Fixed top — order type + table */}
            <div className="flex-shrink-0">
              <div className="px-3 pt-3 pb-1">
                <div className="grid grid-cols-3 gap-1 bg-gray-100 rounded-xl p-1">
                  {ORDER_TYPES.map(({ key, label, Icon }) => (
                    <button key={key} type="button" onClick={() => setOrderType(key)}
                      className={`flex items-center justify-center gap-1 py-1.5 rounded-lg text-[11px] font-semibold transition-all
                        ${orderType === key ? "bg-white text-orange-600 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                      <Icon size={11} /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {orderType === "dine-in" && (
                <div className="px-3 pb-1">
                  <button type="button" onClick={() => setShowTable(true)}
                    className={`w-full flex items-center gap-2 rounded-xl px-3 py-2 transition-all border text-left
                      ${tableNo ? "bg-orange-50 border-orange-200 text-orange-700" : "bg-gray-50 border-gray-200 text-gray-400 hover:border-orange-300"}`}>
                    <TableProperties size={12} className="shrink-0" />
                    <span className="text-xs flex-1">{tableNo ? `Table #${tableNo}` : "Select table…"}</span>
                    <ChevronRight size={12} />
                  </button>
                </div>
              )}
            </div>

            {/* Order items — independent scroll */}
            <div className="flex-1 overflow-y-auto min-h-0 border-y border-gray-100">
              {orderItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[120px] text-gray-300 gap-2 px-5">
                  <ShoppingCart size={28} />
                  <p className="text-xs text-center">Tap a product to add it to the order</p>
                </div>
              ) : (
                orderItems.map(item => (
                  <OrderItemRow key={item.id} item={item} onQty={updateQty} onRemove={removeItem} onNoteChange={updateNote} formatPrice={formatPrice} />
                ))
              )}
            </div>

            {/* Held orders strip */}
            <HeldOrdersPanel held={heldOrders} onRecall={recallOrder} onDiscard={discardHeld} />

            {/* Payment fields — independent scroll */}
            <div className="max-h-[45%] overflow-y-auto min-h-0 scrollbar-thin">
              <div className="px-3 py-3 space-y-2.5">

                {/* Customer */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5 bg-gray-50 rounded-xl px-3 py-2 border border-gray-100">
                    <User size={11} className="text-gray-400 shrink-0" />
                    <input type="text" placeholder="Name" value={customerName} onChange={e => setCustomer(e.target.value)}
                      className="flex-1 bg-transparent text-xs text-gray-700 outline-none placeholder:text-gray-400 min-w-0" />
                  </div>
                  <div className="flex items-center gap-1.5 bg-gray-50 rounded-xl px-3 py-2 border border-gray-100">
                    <Phone size={11} className="text-gray-400 shrink-0" />
                    <input type="tel" placeholder="Phone" value={customerPhone} onChange={e => setPhone(e.target.value)}
                      className="flex-1 bg-transparent text-xs text-gray-700 outline-none placeholder:text-gray-400 min-w-0" />
                  </div>
                </div>

                {/* Order note */}
                <button type="button" onClick={() => setShowOrderNote(v => !v)}
                  className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition">
                  <FileText size={12} />
                  {showOrderNote ? "Hide note" : "Add order note"}
                  {showOrderNote ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                </button>
                {showOrderNote && (
                  <textarea placeholder="Special instructions…" value={orderNote} onChange={e => setOrderNote(e.target.value)}
                    rows={2} className="w-full text-xs border border-gray-200 rounded-xl px-3 py-2 outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-300 placeholder:text-gray-300 bg-gray-50 resize-none" />
                )}

                {/* Discount + Tip */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Discount</p>
                    <div className="flex gap-1">
                      <div className="flex items-center gap-1 flex-1 bg-gray-50 rounded-xl px-2 py-2 border border-gray-100">
                        <Tag size={10} className="text-gray-400 shrink-0" />
                        <input type="number" min="0" placeholder="0" value={discountVal} onChange={e => setDiscount(e.target.value)}
                          className="flex-1 bg-transparent text-xs text-gray-700 outline-none min-w-0 w-0" />
                      </div>
                      <button type="button" onClick={() => setDiscountType(t => t === "fixed" ? "percent" : "fixed")}
                        className="text-[10px] font-bold bg-gray-100 border border-gray-200 rounded-xl px-2 text-gray-600 hover:border-orange-300 hover:text-orange-600 transition shrink-0">
                        {discountType === "fixed" ? "$" : "%"}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Tip</p>
                    <div className="flex gap-1">
                      <div className="flex items-center gap-1 flex-1 bg-gray-50 rounded-xl px-2 py-2 border border-gray-100">
                        <Percent size={10} className="text-gray-400 shrink-0" />
                        <input type="number" min="0" placeholder="0" value={tipVal} onChange={e => setTip(e.target.value)}
                          className="flex-1 bg-transparent text-xs text-gray-700 outline-none min-w-0 w-0" />
                      </div>
                      <button type="button" onClick={() => setTipType(t => t === "percent" ? "fixed" : "percent")}
                        className="text-[10px] font-bold bg-gray-100 border border-gray-200 rounded-xl px-2 text-gray-600 hover:border-orange-300 hover:text-orange-600 transition shrink-0">
                        {tipType === "percent" ? "%" : "$"}
                      </button>
                    </div>
                  </div>
                </div>

              {/* Tip quick presets */}
              <div className="flex gap-1">
                <span className="text-[10px] text-gray-400 self-center shrink-0">Tip:</span>
                {TIP_PRESETS.map(p => (
                  <button key={p} type="button" onClick={() => { setTip(String(p)); setTipType("percent"); }}
                    className={`flex-1 text-[10px] font-bold py-1 rounded-lg border transition
                      ${tipVal === String(p) && tipType === "percent"
                        ? "bg-orange-500 text-white border-orange-500"
                        : "border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-600"}`}>
                    {p}%
                  </button>
                ))}
                <button type="button" onClick={() => setTip("")}
                  className="flex-1 text-[10px] font-bold py-1 rounded-lg border border-gray-200 text-gray-400 hover:border-gray-300 transition">
                  None
                </button>
              </div>

              {/* Totals */}
              <div className="bg-gray-50 rounded-xl px-3 py-2.5 space-y-1 text-xs border border-gray-100">
                <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                {discountAmt > 0 && <div className="flex justify-between text-green-600"><span>Discount{discountType === "percent" ? ` (${discountVal}%)` : ""}</span><span>−{formatPrice(discountAmt)}</span></div>}
                {taxRate > 0 && <div className="flex justify-between text-gray-500"><span>Tax ({storeSettings?.tax_rate || 0}%)</span><span>{formatPrice(tax)}</span></div>}
                {tipAmt > 0 && <div className="flex justify-between text-blue-600"><span>Tip{tipType === "percent" ? ` (${tipVal}%)` : ""}</span><span>+{formatPrice(tipAmt)}</span></div>}
                <div className="flex justify-between font-extrabold text-gray-900 text-sm pt-1 border-t border-gray-200">
                  <span>Total</span><span className="text-orange-600">{formatPrice(total)}</span>
                </div>
              </div>

              {/* Split payment toggle */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Payment</p>
                <button type="button" onClick={() => { setSplitMode(v => !v); setSplitPayments([{ method: "cash", amount: total > 0 ? total.toFixed(2) : "" }]); }}
                  className={`flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg border transition
                    ${splitMode ? "bg-purple-500 text-white border-purple-500" : "border-gray-200 text-gray-500 hover:border-purple-300 hover:text-purple-600"}`}>
                  <Split size={10} /> Split
                </button>
              </div>

              {splitMode ? (
                <SplitPaymentPanel total={total} splitPayments={splitPayments} setSplitPayments={setSplitPayments} formatPrice={formatPrice} />
              ) : (
                <>
                  {/* Payment method */}
                  <div className="grid grid-cols-4 gap-1.5">
                    {METHOD_OPTS.map(({ key, label, Icon }) => (
                      <button key={key} type="button" onClick={() => setMethod(key)}
                        className={`flex flex-col items-center justify-center gap-1 py-2 rounded-xl text-[10px] font-semibold border transition
                          ${method === key ? "bg-orange-500 text-white border-orange-500 shadow-sm" : "bg-white border-gray-200 text-gray-600 hover:border-orange-300"}`}>
                        <Icon size={13} />{label}
                      </button>
                    ))}
                  </div>

                  {/* Cash tendered */}
                  {method === "cash" && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-100">
                        <Banknote size={12} className="text-gray-400 shrink-0" />
                        <input type="number" min="0" step="0.01" placeholder="Cash tendered" value={tendered}
                          onChange={e => setTendered(e.target.value)}
                          className="flex-1 bg-transparent text-xs text-gray-700 outline-none placeholder:text-gray-400" />
                        <button type="button" onClick={() => setShowNumpad(v => !v)}
                          className={`text-[10px] font-bold border rounded-lg px-2 py-1 transition ${showNumpad ? "bg-orange-500 text-white border-orange-500" : "border-gray-200 text-gray-500 hover:border-orange-300"}`}>
                          #
                        </button>
                        {tendered && (
                          <span className={`text-xs font-bold shrink-0 ${change >= 0 ? "text-blue-600" : "text-red-500"}`}>
                            ↩ {formatPrice(change)}
                          </span>
                        )}
                      </div>
                      {showNumpad ? (
                        <Numpad value={tendered} onChange={setTendered} label="Cash Tendered" />
                      ) : (
                        <div className="grid grid-cols-5 gap-1">
                          {[5, 10, 20, 50, 100].map(amt => (
                            <button key={amt} type="button" onClick={() => setTendered(String(amt))}
                              className="text-[11px] py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 transition font-semibold">
                              ${amt}
                            </button>
                          ))}
                          <button type="button" onClick={() => setTendered(String(total.toFixed(2)))}
                            className="col-span-5 text-[11px] py-1.5 rounded-lg border border-orange-200 text-orange-600 hover:bg-orange-50 transition font-bold">
                            Exact — {formatPrice(total)}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              </div>{/* end payment fields */}
            </div>{/* end scrollable body */}

            {/* Sticky charge footer */}
            <div className="flex-shrink-0 border-t border-gray-100 px-3 py-3 bg-white">
              <button type="button" onClick={handleCharge}
                disabled={charging || !orderItems.length || (!splitMode && method === "cash" && (Number(tendered) || 0) < total) || (splitMode && !splitReady)}
                className="w-full py-3 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition shadow-sm flex items-center justify-center gap-2 active:scale-[.98]"
                style={{ background: "linear-gradient(135deg,#f97316,#ea580c)" }}>
                {charging
                  ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing…</>
                  : <><CheckCircle size={16} /> Charge {total > 0 ? formatPrice(total) : ""}</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default POSPage;
